import { Except } from '@common/types/common.type';
import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  autoUpdate,
  useFloating,
} from '@floating-ui/react';
import Button from '@ui/components/atoms/button/button.atom';
import useClickOutside from '@ui/hooks/use-click-outside/use-click-outside.hook';
import { Action } from '@ui/types/common.type';
import { clsxm } from '@ui/utils/style.util';
import {
  ComponentProps,
  ComponentPropsWithoutRef,
  ReactNode,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// #region Types
type DialogBaseProps = Except<ComponentPropsWithoutRef<'dialog'>, 'title' | 'content'>;

/**
 * Props type for Dialog component
 * @property actions - Single action or array of actions to be rendered as buttons
 * @property backdropProps - Props for the FloatingOverlay component
 * @property content - Content to be displayed in the dialog
 * @property onClose - Callback function when dialog is closed
 * @property open - Whether the dialog is open
 * @property title - Optional title for the dialog
 */
export type DialogProps = DialogBaseProps & {
  actions?: Action | Action[];
  actionsContainerClassName?: string;
  actionsContainerProps?: ComponentProps<'footer'>;
  actionsElement?: ReactNode;
  backdropProps?: ComponentProps<typeof FloatingOverlay>;
  content?: ReactNode;
  contentClassName?: string;
  onClose?: VoidFunction;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  title?: ReactNode;
  titleClassName?: string;
};
// #endregion

// #region Styles
const DIALOG_STATE_STYLES = {
  backdrop: {
    false: 'pointer-events-none bg-overlay/0',
    true: 'pointer-events-auto bg-overlay/50',
  },
  dialog: {
    false: 'scale-0 opacity-0',
    true: 'scale-100 opacity-100',
  },
} as const;
// #endregion

/**
 * Dialog component for displaying modal content with actions
 * Features:
 * - Backdrop click to close
 * - Focus management
 * - Portal rendering
 * - Customizable actions
 * - Responsive design
 */
function BaseDialog({
  actions,
  actionsContainerClassName,
  actionsContainerProps,
  actionsElement,
  backdropProps,
  children,
  className,
  content,
  contentClassName,
  onClose,
  onOpenChange,
  open = false,
  title,
  titleClassName,
  ...rest
}: DialogProps) {
  // #region States and Refs
  const [shouldShow, setShouldShow] = useState(open);
  const timeoutId = useRef<NodeJS.Timeout | null>(null);
  // #endregion

  // #region Handlers
  const renderAction = useCallback(
    (
      { children: actionChildren, className: actionClassName, label, ...actionRest }: Action,
      index = 1,
    ) => (
      <Button.Text
        {...actionRest}
        key={index}
        className={clsxm('text-[22px] font-bold', actionClassName)}
      >
        {actionChildren ?? label}
      </Button.Text>
    ),
    [],
  );
  const handleChangeShow = useCallback((openParam: boolean) => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
      timeoutId.current = null;
    }

    timeoutId.current = setTimeout(
      () => {
        setShouldShow(openParam);
      },
      openParam ? 0 : 300,
    );
  }, []);
  const handleOnOpenChange = useCallback(
    (openParam: boolean) => {
      if (!openParam) onClose?.();
      onOpenChange?.(openParam);

      handleChangeShow(openParam);
    },
    [handleChangeShow, onClose, onOpenChange],
  );
  // #endregion

  // #region Effects
  const { context, refs } = useFloating({
    onOpenChange: handleOnOpenChange,
    open,
    whileElementsMounted: autoUpdate,
  });

  useEffect(() => {
    if (open) refs.floating.current?.focus();

    handleChangeShow(open);

    return () => {
      if (timeoutId.current) clearTimeout(timeoutId.current);
    };
  }, [handleChangeShow, open, refs]);

  useClickOutside(refs.floating, onClose);
  // #endregion

  // #region Rendered items
  const actionButtons = useMemo(
    () => (Array.isArray(actions) ? actions.map(renderAction) : !!actions && renderAction(actions)),
    [actions, renderAction],
  );
  // #endregion

  return (
    (open || shouldShow) && (
      <FloatingPortal>
        <FloatingOverlay
          {...backdropProps}
          className={clsxm(
            'flex items-center justify-center z-backdrop duration-200',
            DIALOG_STATE_STYLES.backdrop[`${shouldShow && open}`],
            backdropProps?.className,
          )}
        >
          <FloatingFocusManager context={context} disabled={!open} visuallyHiddenDismiss>
            <dialog
              data-testid="dialog-container"
              {...rest}
              ref={refs.setFloating}
              className={clsxm(
                'flex flex-col gap-y-2.5 items-end rounded-[20px] w-[738px] max-w-[90%] bg-white py-10 px-7 z-dialog duration-200',
                DIALOG_STATE_STYLES.dialog[`${shouldShow && open}`],
                className,
              )}
            >
              {!!title && (
                <header
                  className={clsxm('text-3xl font-semibold text-green-dark w-full', titleClassName)}
                >
                  {title}
                </header>
              )}
              <section
                className={clsxm(
                  'text-[22px] text-green-brown min-h-[112px] w-full grow',
                  contentClassName,
                )}
              >
                {children ?? content}
              </section>
              <footer
                {...actionsContainerProps}
                className={clsxm(
                  'space-x-11',
                  actionsContainerClassName,
                  actionsContainerProps?.className,
                )}
              >
                {actionsElement ?? actionButtons}
              </footer>
            </dialog>
          </FloatingFocusManager>
        </FloatingOverlay>
      </FloatingPortal>
    )
  );
}
BaseDialog.displayName = 'Dialog';

const Dialog = memo(BaseDialog);

export default Dialog;
